
"use client";

import type { ReactNode, Dispatch, SetStateAction } from 'react';
import React, { createContext, useContext, useEffect, useState } from 'react';
import en from '@/locales/en.json';
import es from '@/locales/es.json';
import fr from '@/locales/fr.json';
import de from '@/locales/de.json';
import ja from '@/locales/ja.json';
import it from '@/locales/it.json';
import pt from '@/locales/pt.json';
import ru from '@/locales/ru.json';
import zh from '@/locales/zh.json';
import ar from '@/locales/ar.json';

// Assuming all other locale JSON files are similar to 'en' or can be added later.
// For now, 'en' is the primary source for t() fallback.

export type Locale =
  | 'en' | 'es' | 'fr' | 'de' | 'ja' | 'it' | 'pt' | 'ru' | 'zh' | 'ar'
  | 'bn' | 'bg' | 'ca' | 'hr' | 'cs' | 'da' | 'nl' | 'et' | 'fa' | 'fi'
  | 'el' | 'gu' | 'he' | 'hi' | 'hu' | 'id' | 'kn' | 'ko' | 'lv' | 'lt'
  | 'ms' | 'ml' | 'mr' | 'no' | 'pl' | 'ro' | 'sr' | 'sk' | 'sl' | 'sw'
  | 'sv' | 'ta' | 'te' | 'th' | 'tr' | 'uk' | 'ur' | 'vi';

type Translations = Record<string, string>;

const translations: Record<Locale, Translations> = {
  en,
  es,
  fr,
  de,
  ja,
  it,
  pt,
  ru,
  zh,
  ar,
  // For other languages, they will fallback to 'en' via the t() function if their JSON isn't loaded/mapped here.
  // This setup is primarily for the t() function. languageNames below is for direct display.
  bn: en, bg: en, ca: en, hr: en, cs: en, da: en, nl: en, et: en, fa: en, fi: en,
  el: en, gu: en, he: en, hi: en, hu: en, id: en, kn: en, ko: en, lv: en, lt: en,
  ms: en, ml: en, mr: en, no: en, pl: en, ro: en, sr: en, sk: en, sl: en, sw: en,
  sv: en, ta: en, te: en, th: en, tr: en, uk: en, ur: en, vi: en,
};

interface I18nContextType {
  language: Locale;
  setLanguage: (language: Locale) => void;
  t: (key: string, replacements?: Record<string, string>) => string;
}

const I18nContext = createContext<I18nContextType | undefined>(undefined);

export const I18nProvider = ({ children }: { children: ReactNode }) => {
  const [language, setLanguageState] = useState<Locale>('en'); // Default to English

  useEffect(() => {
    const savedLanguage = localStorage.getItem('chitchat-lang') as Locale | null;
    if (savedLanguage && translations[savedLanguage]) {
      setLanguageState(savedLanguage);
      document.documentElement.lang = savedLanguage;
      document.documentElement.dir = savedLanguage === 'ar' ? 'rtl' : 'ltr';
    } else {
      document.documentElement.lang = language;
      document.documentElement.dir = language === 'ar' ? 'rtl' : 'ltr';
    }
  }, [language]);

  const setLanguage = (lang: Locale) => {
    if (translations[lang]) {
      localStorage.setItem('chitchat-lang', lang);
      setLanguageState(lang);
      document.documentElement.lang = lang;
      document.documentElement.dir = lang === 'ar' ? 'rtl' : 'ltr';
    } else {
      console.warn(`Language "${lang}" not supported by direct JSON mapping. Defaulting to "en".`);
      localStorage.setItem('chitchat-lang', 'en');
      setLanguageState('en');
      document.documentElement.lang = 'en';
      document.documentElement.dir = 'ltr';
    }
  };

  const t = (key: string, replacements?: Record<string, string>): string => {
    let translation = translations[language]?.[key] || translations.en[key] || key;
    if (replacements) {
      Object.keys(replacements).forEach((placeholder) => {
        translation = translation.replace(new RegExp(`{{${placeholder}}}`, 'g'), replacements[placeholder]);
      });
    }
    return translation;
  };
  

  return (
    <I18nContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </I18nContext.Provider>
  );
};

export const useI18n = (): I18nContextType => {
  const context = useContext(I18nContext);
  if (context === undefined) {
    throw new Error('useI18n must be used within an I18nProvider');
  }
  return context;
};

export const locales = Object.keys(translations) as Locale[];

// Expanded languageNames to cover all locales in allCharacterLanguages from types.ts
export const languageNames: Record<Locale, string> = {
  en: 'English',
  es: 'Español', // Spanish
  fr: 'Français', // French
  de: 'Deutsch', // German
  ja: '日本語', // Japanese
  it: 'Italiano', // Italian
  pt: 'Português', // Portuguese
  ru: 'Русский', // Russian
  zh: '简体中文', // Simplified Chinese
  ar: 'العربية', // Arabic
  bn: 'Bengali',
  bg: 'Bulgarian',
  ca: 'Catalan',
  hr: 'Croatian',
  cs: 'Czech',
  da: 'Danish',
  nl: 'Dutch',
  et: 'Estonian',
  fa: 'Farsi', // Persian
  fi: 'Finnish',
  el: 'Greek',
  gu: 'Gujarati',
  he: 'Hebrew',
  hi: 'Hindi',
  hu: 'Hungarian',
  id: 'Indonesian',
  kn: 'Kannada',
  ko: 'Korean',
  lv: 'Latvian',
  lt: 'Lithuanian',
  ms: 'Malay',
  ml: 'Malayalam',
  mr: 'Marathi',
  no: 'Norwegian',
  pl: 'Polish',
  ro: 'Romanian',
  sr: 'Serbian',
  sk: 'Slovak',
  sl: 'Slovenian',
  sw: 'Swahili',
  sv: 'Swedish',
  ta: 'Tamil',
  te: 'Telugu',
  th: 'Thai',
  tr: 'Turkish',
  uk: 'Ukrainian',
  ur: 'Urdu',
  vi: 'Vietnamese',
};
